import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase/config';
import { useAuth } from '@/contexts/AuthContext';
import { validateIdentity } from '@/lib/auth/identity';
import { notifyInvite } from '@/components/manager/AddCustomerDialog';
import { useLanguage } from '@/contexts/LanguageContext';
import { nameTranslationService } from '@/lib/services/nameTranslationService';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import type { UserRole } from '@/types';

const formSchema = z.object({
  firstNameAr: z.string().min(2, 'First name must be at least 2 characters'),
  lastNameAr: z.string().min(2, 'Last name must be at least 2 characters'),
  phone: z.string().default(''),
  email: z.string().default(''),
  role: z.enum(['admin', 'coach', 'customer', 'examiner', 'manager', 'partner', 'volunteer', 'instructor', 'organizer', 'provider', 'coordinator', 'marketer', 'audit'] as const),
}).superRefine((v, ctx) => {
  const r = validateIdentity({ phone: v.phone, email: v.email });
  if (r.ok) return;
  const message =
    r.code === 'required' ? 'Enter a phone number or an email'
    : r.code === 'phone-format' ? 'Phone number must be in format 05XXXXXXXX'
    : 'Enter a valid email address';
  ctx.addIssue({ code: z.ZodIssueCode.custom, path: [r.field], message });
});

const AVAILABLE_ROLES: { value: UserRole; label: string }[] = [
  { value: 'admin', label: 'Administrator' },
  { value: 'coach', label: 'Doctor' },
  { value: 'customer', label: 'Customer' },
  { value: 'examiner', label: 'Examiner' },
  { value: 'manager', label: 'Manager' },
  { value: 'partner', label: 'Partner' },
  { value: 'volunteer', label: 'Volunteer' },
  { value: 'instructor', label: 'Instructor' },
  { value: 'organizer', label: 'Organizer' },
  { value: 'provider', label: 'Provider' },
  { value: 'coordinator', label: 'Coordinator' },
  { value: 'marketer', label: 'Marketer' },
  { value: 'audit', label: 'Audit' }
];

interface AddUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function AddUserDialog({ open, onOpenChange, onSuccess }: AddUserDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { currentUser } = useAuth();

  const { t } = useLanguage();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstNameAr: '',
      lastNameAr: '',
      phone: '',
      email: '',
      role: 'customer',
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    if (!currentUser) {
      toast.error('Admin authentication required');
      return;
    }

    const id = validateIdentity({ phone: values.phone, email: values.email });
    if (!id.ok) return;

    let result: { uid: string; identity: 'phone' | 'email' | 'both' } | null = null;
    try {
      setIsLoading(true);

      const { firstNameEn, lastNameEn } = await nameTranslationService.translateToEnglish(
        values.firstNameAr,
        values.lastNameAr,
      );

      const createManagedUser = httpsCallable<
        {
          firstNameEn: string;
          lastNameEn: string;
          firstNameAr: string;
          lastNameAr: string;
          phone?: string;
          email?: string;
          role: z.infer<typeof formSchema>['role'];
        },
        { uid: string; identity: 'phone' | 'email' | 'both' }
      >(functions, 'createManagedUser');

      const { data } = await createManagedUser({
        firstNameEn,
        lastNameEn,
        firstNameAr: values.firstNameAr,
        lastNameAr: values.lastNameAr,
        ...(id.phone ? { phone: id.phone } : {}),
        ...(id.email ? { email: id.email } : {}),
        role: values.role,
      });
      result = data;
    } catch (error: any) {
      console.error('Error creating user:', error);
      const msg = String(error?.message || '');
      if (msg.includes('email-already-registered')) {
        toast.error('This email is already registered to another user');
      } else if (msg.includes('phone-or-email-required')) {
        toast.error('Enter a phone number or an email');
      } else if (msg.includes('phone-already-registered')) {
        toast.error('This phone number is already registered to another user');
      } else if (error?.code === 'functions/permission-denied' || msg.includes('admin required')) {
        toast.error('You are not authorized to create this user');
      } else if (error?.code === 'functions/unauthenticated' || msg.includes('sign-in required')) {
        toast.error('Your session expired, please sign in again');
      } else {
        toast.error('Failed to create user');
      }
    } finally {
      setIsLoading(false);
    }

    if (!result) return;

    // Post-success work runs outside the try: a throw here (e.g. the invite
    // email failing to send) must never misreport a created account as
    // "Failed to create user" or leave the dialog open.
    toast.success('User account created successfully');
    if (result.identity !== 'phone' && id.email) await notifyInvite(id.email, t);
    if (result.identity !== 'email') toast.info('They sign in with their phone number via a one-time code (WhatsApp/SMS) — no password');
    form.reset();
    onSuccess?.();
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add New User</DialogTitle>
          <DialogDescription>
            Create a new user account. They will be able to log in using the generated credentials.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstNameAr"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name (Arabic)</FormLabel>
                    <FormControl>
                      <Input placeholder="محمد" disabled={isLoading} {...field} dir="rtl" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastNameAr"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name (Arabic)</FormLabel>
                    <FormControl>
                      <Input placeholder="أحمد" disabled={isLoading} {...field} dir="rtl" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <Input
                      type="tel"
                      placeholder="05XXXXXXXX"
                      maxLength={14}
                      disabled={isLoading}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" autoComplete="off" placeholder="name@example.com" disabled={isLoading} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <p className="text-xs text-muted-foreground">Enter a phone number, an email, or both</p>

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Initial Role</FormLabel>
                  <Select
                    disabled={isLoading}
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {AVAILABLE_ROLES.map((role) => (
                        <SelectItem key={role.value} value={role.value}>
                          {role.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create User'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}